import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Mail,
  Sun,
  Moon,
  FileText,
  Shield,
  ScrollText
} from "lucide-react";
import { useTheme } from "../App";

const translations = {
  fr: {
    back: "Retour à l'accueil",
    contact: "Contact : contact@alia-assistant.com",
    legal: {
      title: "Mentions Légales",
      content: `
## Éditeur du site

**ALIA** est un logiciel SaaS édité par **HM CONSEIL**,
SAS immatriculée au Registre du Commerce et des Sociétés de Versailles
sous le numéro 978 948 545.

**Email :** contact@alia-assistant.com
**Site :** https://alia-assistant.com

## Hébergement

Le site est hébergé en Europe, conformément au RGPD.

## Propriété intellectuelle

L'ensemble du contenu de ce site (textes, images, logos, marques, etc.) est protégé par le droit de la propriété intellectuelle. Toute reproduction, représentation, modification ou exploitation non autorisée est interdite.

## Responsabilité

ALIA s'efforce d'assurer l'exactitude des informations diffusées sur ce site. Toutefois, ALIA ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition.

## Droit applicable

Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.

*Dernière mise à jour : Décembre 2025*
      `
    },
    privacy: {
      title: "Politique de Confidentialité",
      content: `
## Introduction

ALIA s'engage à protéger la vie privée des utilisateurs de son site web et de ses services. Cette politique de confidentialité décrit les données que nous collectons et comment nous les utilisons.

## Données collectées

### Via les formulaires de contact et de démonstration
- Nom et prénom
- Adresse email professionnelle
- Nom de l'entreprise
- Fonction
- Taille de l'équipe
- Message (optionnel)

### Données de connexion
- Adresse IP
- Type de navigateur
- Pages visitées

## Utilisation des données

Vos données sont utilisées pour :
- Répondre à vos demandes de contact ou de démonstration
- Vous contacter concernant nos services
- Améliorer notre site et nos services
- Respecter nos obligations légales

## Conservation des données

Vos données personnelles sont conservées pendant une durée maximale de 3 ans à compter de votre dernier contact avec nous, sauf obligation légale contraire.

## Vos droits

Conformément au RGPD, vous disposez des droits suivants :
- Droit d'accès à vos données
- Droit de rectification
- Droit à l'effacement ("droit à l'oubli")
- Droit à la limitation du traitement
- Droit à la portabilité
- Droit d'opposition

Pour exercer ces droits, contactez-nous à : **contact@alia-assistant.com**

## Cookies

Notre site utilise des cookies essentiels au fonctionnement du site. Aucun cookie publicitaire ou de tracking n'est utilisé.

## Hébergement des données

Vos données sont hébergées en Europe, conformément au RGPD.

## Contact

Pour toute question relative à cette politique de confidentialité :
**Email :** contact@alia-assistant.com

*Dernière mise à jour : Décembre 2025*
      `
    },
    terms: {
      title: "Conditions Générales de Vente",
      content: `
## Article 1 - Objet

Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre HM CONSEIL, éditeur du logiciel ALIA, et ses clients professionnels pour la fourniture de services de gestion intelligente des emails.

## Article 2 - Services proposés

ALIA propose une plateforme d'intelligence email pour l'écosystème Microsoft Outlook, incluant :
- Priorisation intelligente des emails
- Recommandations d'actions
- Suivi automatisé
- Intégration native Microsoft Outlook

## Article 3 - Processus commercial

ALIA fonctionne selon un modèle commercial dirigé par les ventes (sales-led) :
- Les tarifs sont communiqués sur devis après analyse des besoins
- Aucun paiement par carte bancaire dans l'application
- La facturation est effectuée sur devis uniquement

## Article 4 - Tarification

- Les tarifs sont établis sur devis personnalisé
- Le prix est déterminé en fonction du nombre d'utilisateurs et des fonctionnalités requises
- Aucune tarification publique n'est affichée

## Article 5 - Facturation et paiement

- Facturation sur devis uniquement
- Modes de paiement acceptés : virement bancaire, prélèvement SEPA
- **Aucun paiement par carte bancaire**
- Délai de paiement : 30 jours à compter de la date de facture

## Article 6 - Durée et résiliation

- Les contrats sont établis pour une durée déterminée selon les termes du devis
- Les conditions de résiliation sont précisées dans chaque contrat individuel

## Article 7 - Protection des données

HM CONSEIL s'engage à protéger les données de ses clients conformément au RGPD et à sa Politique de Confidentialité.

## Article 8 - Responsabilité

HM CONSEIL s'engage à fournir ses services avec diligence. La responsabilité de HM CONSEIL est limitée aux dommages directs et prévisibles.

## Article 9 - Droit applicable

Les présentes CGV sont soumises au droit français. Tout litige sera soumis à la compétence exclusive des tribunaux de Versailles.

## Article 10 - Contact

Pour toute question relative aux présentes CGV :
**Email :** contact@alia-assistant.com

*Dernière mise à jour : Décembre 2025*
      `
    }
  },
  en: {
    back: "Back to home",
    contact: "Contact: contact@alia-assistant.com",
    legal: {
      title: "Legal Notice",
      content: `
## Publisher

**ALIA** is a SaaS software operated by **HM CONSEIL**,
a French SAS registered with the Trade and Companies Register of Versailles
under number 978 948 545.

**Email:** contact@alia-assistant.com
**Website:** https://alia-assistant.com

## Hosting

The site is hosted in Europe, in compliance with GDPR.

## Intellectual Property

All content on this site (texts, images, logos, trademarks, etc.) is protected by intellectual property law. Any unauthorized reproduction, representation, modification or exploitation is prohibited.

## Liability

ALIA strives to ensure the accuracy of information published on this site. However, ALIA cannot guarantee the accuracy, precision or completeness of the information provided.

## Applicable Law

These legal notices are subject to French law. In case of dispute, French courts will have sole jurisdiction.

*Last updated: December 2025*
      `
    },
    privacy: {
      title: "Privacy Policy",
      content: `
## Introduction

ALIA is committed to protecting the privacy of users of its website and services. This privacy policy describes the data we collect and how we use it.

## Data Collected

### Via contact and demo request forms
- First and last name
- Professional email address
- Company name
- Job title
- Team size
- Message (optional)

### Connection data
- IP address
- Browser type
- Pages visited

## Use of Data

Your data is used to:
- Respond to your contact or demo requests
- Contact you regarding our services
- Improve our site and services
- Comply with our legal obligations

## Data Retention

Your personal data is retained for a maximum of 3 years from your last contact with us, unless a legal obligation requires otherwise.

## Your Rights

Under the GDPR, you have the following rights:
- Right of access to your data
- Right of rectification
- Right to erasure ("right to be forgotten")
- Right to restriction of processing
- Right to data portability
- Right to object

To exercise these rights, contact us at: **contact@alia-assistant.com**

## Cookies

Our site uses cookies essential for site operation. No advertising or tracking cookies are used.

## Data Hosting

Your data is hosted in Europe, in compliance with GDPR.

## Contact

For any questions regarding this privacy policy:
**Email:** contact@alia-assistant.com

*Last updated: December 2025*
      `
    },
    terms: {
      title: "Terms of Service",
      content: `
## Article 1 - Purpose

These Terms of Service govern the contractual relationships between HM CONSEIL, publisher of the ALIA software, and its professional clients for the provision of intelligent email management services.

## Article 2 - Services Offered

ALIA offers an email intelligence platform for the Microsoft Outlook ecosystem, including:
- Intelligent email prioritization
- Action recommendations
- Automated follow-up
- Native Microsoft Outlook integration

## Article 3 - Sales Process

ALIA operates on a sales-led commercial model:
- Pricing is communicated via quote after needs analysis
- No credit card payment in the application
- Billing is by quote only

## Article 4 - Pricing

- Prices are established via personalized quote
- Price is determined based on number of users and required features
- No public pricing is displayed

## Article 5 - Billing and Payment

- Billing by quote only
- Accepted payment methods: bank transfer, SEPA direct debit
- **No credit card payment**
- Payment term: 30 days from invoice date

## Article 6 - Duration and Termination

- Contracts are established for a fixed term according to quote terms
- Termination conditions are specified in each individual contract

## Article 7 - Data Protection

HM CONSEIL is committed to protecting client data in accordance with GDPR and its Privacy Policy.

## Article 8 - Liability

HM CONSEIL commits to providing its services with diligence. HM CONSEIL's liability is limited to direct and foreseeable damages.

## Article 9 - Applicable Law

These Terms of Service are subject to French law. Any dispute will be subject to the exclusive jurisdiction of the Versailles courts.

## Article 10 - Contact

For any questions regarding these Terms of Service:
**Email:** contact@alia-assistant.com

*Last updated: December 2025*
      `
    }
  }
};

const LegalPage = ({ pageType }) => {
  const { theme, toggleTheme } = useTheme();
  const [lang, setLang] = useState("fr");
  const t = translations[lang];
  
  const pageData = t[pageType] || t.legal;

  const icons = {
    legal: FileText,
    privacy: Shield,
    terms: ScrollText
  };
  const Icon = icons[pageType] || FileText;

  // Simple markdown-like rendering
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-xl font-semibold text-slate-900 dark:text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-lg font-medium text-slate-800 dark:text-slate-200 mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-slate-600 dark:text-slate-400 ml-4">{line.replace('- ', '')}</li>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-slate-800 dark:text-slate-200">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.startsWith('*') && line.endsWith('*')) {
        return <p key={i} className="text-sm italic text-slate-500 dark:text-slate-400 mt-4">{line.replace(/\*/g, '')}</p>;
      }
      if (line.trim() === '') {
        return <br key={i} />;
      }
      // Handle inline bold
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/);
        return (
          <p key={i} className="text-slate-600 dark:text-slate-400 mb-2">
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-slate-800 dark:text-slate-200">{part}</strong> : part)}
          </p>
        );
      }
      return <p key={i} className="text-slate-600 dark:text-slate-400 mb-2">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>

            <div className="flex items-center gap-3">
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLang("fr")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "fr" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "en" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  EN
                </button>
              </div>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-slate-400" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200 dark:border-slate-700">
              <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {pageData.title}
              </h1>
            </div>

            <div className="prose dark:prose-invert max-w-none">
              {renderContent(pageData.content)}
            </div>

            <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t.contact}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Export individual page components
export const LegalNoticePage = () => <LegalPage pageType="legal" />;
export const PrivacyPolicyPage = () => <LegalPage pageType="privacy" />;
export const TermsOfServicePage = () => <LegalPage pageType="terms" />;

export default LegalPage;
